import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';

import { config } from 'src/utils/config';
import messages from './messages';

/**
 * In-app Stripe Embedded Checkout modal.
 *
 * Mounts Stripe's EmbeddedCheckout (an iframe) inside our own overlay so plan
 * upgrades + AI-credit top-ups happen without leaving the page. Driven by
 * `useStripeCheckout`: `clientSecret` (from the backend session) opens it;
 * Stripe calls `onComplete` when the session completes (session created with
 * `redirect_on_completion='never'`), and the close control calls `onClose`.
 *
 * `loadStripe` is called once at module scope (per Stripe guidance — avoids
 * recreating the Stripe object on every render).
 */
const stripePromise = config.stripePublishableKey
  ? loadStripe(config.stripePublishableKey)
  : null;

// `loadStripe` REJECTS when Stripe.js refuses the publishable key, and resolves
// to null when js.stripe.com could not be fetched at all (offline, blocked by an
// extension, CSP). `EmbeddedCheckoutProvider` handles neither: it awaits the
// promise and simply never renders, so the modal below would mount as an empty
// panel with nothing in it but the close button — no error, no fallback copy,
// and the backend session already created. That is precisely how a bad key took
// prod's self-serve checkout down invisibly (see utils/stripeKey.ts).
//
// Settle the outcome once, here at module scope rather than in the component,
// for two reasons: the modal may never mount, and an unattended rejection
// reaches the console as a bare "Uncaught (in promise) IntegrationError" with
// nothing in it pointing at billing.
const stripeReady: Promise<boolean> = stripePromise
  ? stripePromise.then(
    (stripe) => stripe !== null,
    (e) => {
      // eslint-disable-next-line no-console
      console.error(
        '[Stripe] Stripe.js failed to load — self-serve checkout is unavailable', e,
      );
      return false;
    },
  )
  : Promise.resolve(false);

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 1000,
  overflowY: 'auto',
  padding: '32px 16px',
};

const panelStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  width: '100%',
  maxWidth: 640,
  padding: '16px',
  position: 'relative',
};

const errorStyle: React.CSSProperties = {
  margin: '24px 8px 8px',
  color: '#b42318',
  fontSize: 14,
  lineHeight: 1.5,
};

const closeStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 12,
  border: 'none',
  background: 'transparent',
  fontSize: 24,
  lineHeight: 1,
  cursor: 'pointer',
};

const CheckoutModal: React.FC<{
  clientSecret: string | null;
  onComplete: () => void;
  onClose: () => void;
}> = ({ clientSecret, onComplete, onClose }) => {
  // null = still resolving; render the provider and let Stripe show its own
  // loader. Only an explicit false swaps in the error copy.
  const [stripeOk, setStripeOk] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    stripeReady.then((ok) => {
      if (!cancelled) setStripeOk(ok);
    });
    return () => { cancelled = true; };
  }, []);

  if (!clientSecret || !stripePromise) return null;
  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={panelStyle}>
        <button
          type="button"
          style={closeStyle}
          aria-label="Close"
          onClick={onClose}
        >
          &times;
        </button>
        {stripeOk === false ? (
          <p style={errorStyle} role="alert">
            <FormattedMessage {...messages.checkoutUnavailable} />
          </p>
        ) : (
          /* key forces a fresh mount per session so a new client_secret
             re-initialises the embedded checkout. */
          <EmbeddedCheckoutProvider
            key={clientSecret}
            stripe={stripePromise}
            options={{ clientSecret, onComplete }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
